import React, {useMemo} from 'react';
import {SelectDropdown} from '../../../../../../../../elements/select/SelectDropdown';
import {DeleteButton} from '../../../../../../../../elements/deleteButton/DeleteButton';
import {ClickAction, UpdateVehicleDetailsButtonRequest} from '../../../../../../../../../api/generated';
import styles from './VehicleButton.module.css';
import {
    ValidatedPromptInputField
} from '../../../../../../../../elements/validator/component/ValidatedPromptInputField';
import {NotEmptyStringValidator} from '../../../../../../../../elements/validator/Validator';
import {OnValidChangeHandler} from '../../../../../../../../elements/validator/component/ValidationComponent';
import {makeSelectVehicleButtonMessage, makeSelectVehicleButtonText,} from "../../../../widgetAppearanceUpdateSlice";

interface VehicleButtonProps {
    button: UpdateVehicleDetailsButtonRequest;
    index: number;
    onUpdate: (index: number, updates: Partial<UpdateVehicleDetailsButtonRequest>) => void;
    onDelete: (index: number) => void;
    isTranslationMode: boolean;
    getMessageWrapper: (fieldName: string) => string;
    updateValidationStatus: OnValidChangeHandler<UpdateVehicleDetailsButtonRequest>;
}

// Room for translated labels, which often run longer than the English ones
const BUTTON_TEXT_MAX_LENGTH = 30;

const clickActionOptions = [
    {label: 'Vehicle Redirect', value: ClickAction.RedirectToDetailsPage},
    {label: 'Aime Message', value: ClickAction.SendMessage}
];

export const VehicleButton: React.FC<VehicleButtonProps> = ({
                                                                button,
                                                                onUpdate,
                                                                onDelete,
                                                                isTranslationMode,
                                                                getMessageWrapper,
                                                                updateValidationStatus,
                                                                index
                                                            }) => {
    const buttonTextSelector = useMemo(
        () => makeSelectVehicleButtonText(index),
        [index]
    );

    const buttonMessageSelector = useMemo(
        () => makeSelectVehicleButtonMessage(index),
        [index]
    );

    const buttonTextFieldName = `Button ${index + 1} Text` as keyof UpdateVehicleDetailsButtonRequest;
    const buttonMessageFieldName = `Button ${index + 1} Message` as keyof UpdateVehicleDetailsButtonRequest;

    return (
        <div className={styles.buttonSection}>
            <div className={styles.buttonHeader}>
                <h3 className={styles.buttonTitle}>Button {index + 1}</h3>
                <div className={styles.deleteWrapper}>
                    <DeleteButton
                        disabled={isTranslationMode}
                        handleClick={() => !isTranslationMode && onDelete(index)}
                    />
                </div>
            </div>

            <div className={styles.fieldSection}>
                <label className={styles.fieldLabel}>Button Text Customisation</label>
                <ValidatedPromptInputField
                    validator={NotEmptyStringValidator}
                    selector={buttonTextSelector}
                    fieldName={buttonTextFieldName}
                    onChange={(value) => onUpdate(index, {buttonText: value})}
                    updateValidationStatus={updateValidationStatus}
                    label="Button Text"
                    placeholder="View Vehicle"
                    disabled={false}
                    getMessage={getMessageWrapper}
                    maxLength={BUTTON_TEXT_MAX_LENGTH}
                />
            </div>

            <div className={styles.fieldSection}>
                <label className={styles.selectFieldLabel}>Actions</label>
                <SelectDropdown
                    title=""
                    options={clickActionOptions}
                    value={button.clickAction}
                    disabled={isTranslationMode}
                    handleChange={(value) => onUpdate(index, {clickAction: value as ClickAction})}
                />
            </div>

            <div className={styles.fieldSection}>
                <label
                    className={button.clickAction === ClickAction.SendMessage ? styles.fieldLabel : styles.disabledFieldLabel}>
                    Message Sent
                </label>
                <ValidatedPromptInputField
                    validator={NotEmptyStringValidator}
                    selector={buttonMessageSelector}
                    fieldName={buttonMessageFieldName}
                    onChange={(value) => onUpdate(index, {message: value})}
                    updateValidationStatus={updateValidationStatus}
                    label="Message"
                    placeholder="Message"
                    disabled={button.clickAction !== ClickAction.SendMessage}
                    required={button.clickAction === ClickAction.SendMessage}
                    getMessage={getMessageWrapper}
                    maxLength={50}
                />
            </div>
        </div>
    );
};
